import React, { useMemo } from 'react'
import { BtnV2, l, TextArea } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { ReasonListLabels } from 'common/billing/constants/reasonListLabels'
import { getHasChildReasons } from 'common/billing/entities/subscription/selectors'
import { CancellationReasonType } from 'common/billing/interfaces/cancellationReasons'
import { CancellationReason } from 'shared/api/requests/billing/schemas'
import { analyticsService } from 'utils/services/analytics'

import CancelReason from './CancelReason'
import leaveSvg from './leave.svg'

interface IReasonListStep {
  description: string
  reasons: CancellationReason[]
  reason?: CancellationReasonType | null
  name: string
  onChange: (value: CancellationReasonType) => void
  onClose: () => void
  onNextStep: () => void
  onChangeDescription: (value: string) => void
}

const ReasonListStep = ({
  description,
  reasons,
  reason,
  onClose,
  onNextStep,
  onChange,
  onChangeDescription,
}: IReasonListStep) => {
  const hasChildren = useAppSelector((state) => getHasChildReasons(state, reason))
  const options = reasons
    .filter((r) => r.parent === null)
    .map((reason) => ({
      id: reason.id,
      value: reason.id,
      label:
        reason.id in ReasonListLabels ? l.getString(ReasonListLabels[reason.id]) : reason.label,
    }))
  const shouldShowTextInput =
    ['other', CancellationReasonType.OTHER_MAIN].includes(reason as string) ||
    reason?.endsWith('_other')
  const disabledSubmit = !reason || hasChildren || (shouldShowTextInput && !description)

  const placeholderText = useMemo(() => {
    const otherToolIds = [
      CancellationReasonType.BETTER_FEATURES,
      CancellationReasonType.MORE_AFFORDABLE,
      CancellationReasonType.BETTER_INTEGRATION,
      CancellationReasonType.BETTER_SUPPORT,
      CancellationReasonType.MORE_AI_AUTOMATION,
    ]
    if (otherToolIds.includes(reason as CancellationReasonType)) {
      return l.translate('What tool did you switched too?')
    }

    if (reason === 'platform_switch') {
      return l.translate('What will you use? What do you miss?')
    }

    return l.translate('Please, enter a description')
  }, [reason])

  const handleChange = (value: string) => {
    onChange(value as CancellationReasonType)
  }

  const handleChangeDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target
    onChangeDescription(value)
  }

  const handleClose = () => {
    onClose()
    analyticsService.sendEvent('SETTINGS.BILLING.CANCEL_MODAL.REASON_LIST.CLOSE')
  }

  const handleChangeStep = () => {
    onNextStep()
    analyticsService.sendEvent('SETTINGS.BILLING.CANCEL_MODAL.REASON_LIST.NEXT', { reason })
  }

  React.useEffect(() => {
    analyticsService.sendEvent('SETTINGS.BILLING.CANCEL_MODAL.REASON_LIST.SHOW')
  }, [])

  return (
    <>
      <div className="rounded-top d-flex justify-center p-a" style={{ backgroundColor: '#96dae2' }}>
        <img className="d-block" src={leaveSvg} alt={l.translate('leave')} />
      </div>
      <div className="p-a-lg">
        <h1>{l.translate('We’re sorry to see you go!')}</h1>
        <p className="m-b-md text-subheading">
          {l.translate('Your feedback helps us improve. What is the main reason for canceling?')}
        </p>
        {options.map((option) => (
          <CancelReason
            key={option.id}
            value={reason}
            reasons={reasons}
            options={[option]}
            onChange={handleChange}
          />
        ))}
        {shouldShowTextInput && (
          <>
            <span className="text-subheading m-t-md d-block" id="textarea-reason">
              {l.translate('Please, tell us more about your reason:')}
            </span>
            <TextArea
              placeholder={placeholderText}
              ariaLabelledBy="textarea-reason"
              className="m-t-xs"
              value={description}
              onChange={handleChangeDescription}
              minRows={2}
              maxRows={3}
            />
          </>
        )}
      </div>
      <div className="p-y-xs p-x b-t row between">
        <BtnV2 onClick={handleClose}>{l.translate('I change my mind')}</BtnV2>
        <BtnV2
          data-test-id="confirm-cancel-btn"
          disabled={disabledSubmit}
          onClick={handleChangeStep}
          variant="primary"
        >
          {l.translate('Next')}
        </BtnV2>
      </div>
    </>
  )
}

export default ReasonListStep
